import React, { useState, useEffect, useContext } from "react";
import { useLocation, Link as RouterLink, useHistory } from "react-router-dom";
import { 
  Container, 
  CssBaseline, 
  Typography, 
  Button, 
  CircularProgress, 
  Box, 
  Paper
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CheckCircleOutline, ErrorOutline, MailOutline } from "@material-ui/icons";
import { openApi } from "../../services/api";
import ColorModeContext from "../../layout/themeContext";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#00B2FF",
    backgroundImage: "linear-gradient(326deg, rgba(0, 178, 255, 1) 0%, rgba(0, 70, 249, 1) 50%, rgba(0, 4, 25, 1) 100%)",
  },
  paper: {
    padding: theme.spacing(6),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: "16px",
    textAlign: "center",
    maxWidth: "500px",
    width: "90%",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
  },
  iconSuccess: {
    fontSize: 80,
    color: "#10b981", // Verde
    marginBottom: theme.spacing(2),
  },
  iconError: {
    fontSize: 80,
    color: "#ef4444", // Vermelho
    marginBottom: theme.spacing(2),
  },
  submit: {
    margin: theme.spacing(4, 0, 2),
    padding: "10px 30px",
    borderRadius: "8px",
  },
}));

const VerifyEmail = () => {
  const classes = useStyles();
  const { colorMode } = useContext(ColorModeContext);
  const { appName, appLogoFavicon } = colorMode;
  const history = useHistory();
  const { handleLogin } = useContext(AuthContext);
  
  const location = useLocation();
  const useQuery = () => new URLSearchParams(location.search);
  const query = useQuery();
  const token = query.get("token");
  const email = query.get("email");
  const password = location.state?.password;

  // Estados possíveis: 'verifying' (carregando token), 'waiting' (aguardando clique), 'success', 'error'
  const [mode, setMode] = useState("verifying");
  const [message, setMessage] = useState("");
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (token) {
      setMode("verifying");
      const verifyToken = async () => {
        try {
          await openApi.get(`/verify-email?token=${token}`);
          setMode("success");
          setMessage("Sua conta foi ativada com sucesso! Você já pode fazer o login na plataforma.");
        } catch (err) {
          setMode("error");
          setMessage(err.response?.data?.error || "Ocorreu um erro ao validar sua conta. O link pode ter expirado.");
        }
      };
      verifyToken();
    } else if (email) {
      setMode("waiting");
    } else {
      setMode("error");
      setMessage("Link de verificação inválido ou ausente.");
    }
  }, [token, email]);

  const handleCheckVerification = async (isManual = false) => {
    try {
      const { data } = await openApi.get('/verify-email/check', { params: { email } });
      if (data.isVerified) {
        if (password) {
          await handleLogin({ email, password });
          // Fallback de segurança: caso o handleLogin falhe silenciosamente (ex: erro 401 de credencial),
          // nós forçamos a tela de sucesso para o usuário não ficar preso na tela de aguardando.
          setMode("success");
          setMessage("Sua conta já está ativada! Se não foi redirecionado, clique abaixo para acessar.");
        } else {
          setMode("success");
          setMessage("Sua conta foi ativada com sucesso! Você já pode fazer o login na plataforma.");
        }
      } else if (isManual) {
        toast.info("Ainda aguardando verificação. Verifique sua caixa de entrada.");
      }
    } catch (err) {
      console.error("Erro na checagem de verificação:", err);
      if (isManual) {
        toast.error(err.response?.data?.error || "Erro de conexão ao verificar o status da sua conta.");
      }
    }
  };

  useEffect(() => {
    let pollInterval = null;
    if (mode === "waiting" && email) {
      pollInterval = setInterval(() => {
        handleCheckVerification(false);
      }, 100000); // Roda a cada 1 minuto e 40 segundos para evitar sobrecarga no servidor
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [mode, email]);

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      await openApi.post('/verify-email/resend', { email });
      toast.success("E-mail de confirmação reenviado com sucesso!");
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Erro ao reenviar o e-mail.";
      console.error("Erro ao reenviar e-mail:", errorMsg);
      // Mostra uma mensagem mais amigável para o usuário, mas loga o erro real.
      toast.error("Não foi possível reenviar o e-mail. Tente novamente mais tarde.");
      // Para administradores, poderia ser útil ter um erro mais específico
      // toast.error(errorMsg); 
    }
    setIsResending(false);
  };

  const handleCancelRegistration = async () => {
    try {
      await openApi.delete('/verify-email/cancel', { data: { email } });
      history.push("/signup");
    } catch (err) {
      toast.error("Erro ao cancelar o cadastro. Pode já estar verificado.");
    }
  };

  return (
    <div className={classes.root}>
      <Helmet>
        <title>{appName || "CliqBR"} - Validação de Conta</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>
      <Container component="main" maxWidth="sm" style={{ display: "flex", justifyContent: "center", }}>
        <CssBaseline />
        <Paper className={classes.paper}>
          {mode === "verifying" && (
            <CircularProgress size={60} />
          )}
          
          {mode === "waiting" && (
            <>
              <MailOutline style={{ fontSize: 80, color: "#0067E3", marginBottom: 16 }} />
              <Typography variant="h5" gutterBottom>Verifique seu E-mail</Typography>
              <Typography variant="body1" color="textSecondary" style={{ marginBottom: 16 }}>
                Enviamos um link de confirmação para:<br />
                <strong>{email}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                Por favor, clique no link recebido para ativar sua conta. Lembre-se de checar a caixa de Spam.
              </Typography>
            <Button variant="contained" color="primary" fullWidth onClick={handleResendEmail} disabled={isResending} style={{ marginBottom: 16 }}>
              {isResending ? <CircularProgress size={24} color="inherit" /> : "Reenviar e-mail"}
              </Button>
              <Button variant="outlined" color="primary" fullWidth onClick={() => handleCheckVerification(true)} style={{ marginBottom: 16 }}>
                Já confirmei, concluir verificação
              </Button>
              <Button variant="text" color="secondary" fullWidth onClick={handleCancelRegistration}>
                Não é este e-mail? Refazer cadastro
              </Button>
            </>
          )}

          {mode === "success" && (
            <>
              <CheckCircleOutline className={classes.iconSuccess} />
              <Typography variant="h5" gutterBottom>E-mail Verificado!</Typography>
              <Typography variant="body1" color="textSecondary">{message}</Typography>
              <Button component={RouterLink} to="/login" variant="contained" color="primary" className={classes.submit}>
                Ir para o Login
              </Button>
            </>
          )}
          
          {mode === "error" && (
            <>
              <ErrorOutline className={classes.iconError} />
              <Typography variant="h5" gutterBottom>Falha na Verificação</Typography>
              <Typography variant="body1" color="textSecondary">{message}</Typography>
              <Button component={RouterLink} to="/login" variant="outlined" color="primary" className={classes.submit}>
                Voltar
              </Button>
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default VerifyEmail;